import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import ProjectTypePicker, { ProjectTypePills } from '../components/ProjectTypePicker';
import ProjectBoardTab from '../components/ProjectBoardTab';
import ProjectCalendarTab from '../components/ProjectCalendarTab';
import ProjectListTab from '../components/ProjectListTab';
import ProjectTimelineTab from '../components/ProjectTimelineTab';
import ProjectChecklistsTab from '../components/ProjectChecklistsTab';
import ProjectRolesTab from '../components/ProjectRolesTab';
import { useAuth } from '../context/AuthContext';
import { fetchProject, updateProject, type ProjectRow, type ProjectStatus } from '../api/projects';
import { fetchVerifiedUsers } from '../api/users';
import { formatTaskDate, toDateInputValue } from '../utils/formatTaskDate';
import './Page.css';
import './ClientDetail.css';
import './ProjectDetail.css';

const TABS = ['Overview', 'Board', 'List', 'Calendar', 'Timeline', 'Checklists', 'Roles'] as const;
type TabId = (typeof TABS)[number];

/** Format a datetime-local value ("2026-06-10T14:30") for display; falls back to the raw string. */
function formatEventDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function hasTfriRole(user: { roles?: string[] } | null | undefined): boolean {
  const roles = user?.roles;
  return Array.isArray(roles) && roles.includes('admin');
}

function canEditProject(userId: number | undefined, project: ProjectRow | null, tfri: boolean): boolean {
  if (!userId || !project) return false;
  if (tfri) return true;
  if (project.created_by_user_id === userId) return true;
  return project.collaborators.some((c) => c.user_id === userId);
}

function taskCount(n: number): string {
  return `${n} task${n === 1 ? '' : 's'}`;
}

function statusChangeNotice(from: ProjectStatus, to: ProjectStatus, tasksUpdated: number): string {
  if (to === 'On Hold') {
    return tasksUpdated > 0
      ? `Project put on hold. ${taskCount(tasksUpdated)} moved to On Hold.`
      : 'Project put on hold. It had no open tasks to put on hold.';
  }
  if (to === 'Closed') return 'Project closed.';
  const label = from === 'On Hold' ? 'Project resumed.' : 'Project reopened.';
  return tasksUpdated > 0 ? `${label} ${taskCount(tasksUpdated)} went back to their earlier status.` : label;
}

const ProjectDetail: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const idNum = projectId ? parseInt(projectId, 10) : NaN;

  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const initialTab = ((): TabId => {
    const t = searchParams.get('tab');
    return TABS.includes(t as TabId) ? (t as TabId) : 'Overview';
  })();
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);

  const selectTab = useCallback(
    (tab: TabId) => {
      setActiveTab(tab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'Overview') next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [verifiedUsers, setVerifiedUsers] = useState<{ id: number; username: string }[]>([]);

  const [addCollabOpen, setAddCollabOpen] = useState(false);
  const [addCollabIds, setAddCollabIds] = useState<number[]>([]);
  const [addCollabSaving, setAddCollabSaving] = useState(false);
  const [addCollabError, setAddCollabError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editCollabIds, setEditCollabIds] = useState<number[]>([]);
  const [editProjectTypes, setEditProjectTypes] = useState<string[]>([]);
  const [editEventLocation, setEditEventLocation] = useState('');
  const [editEventStart, setEditEventStart] = useState('');
  const [editEventEnd, setEditEventEnd] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [statusSaving, setStatusSaving] = useState<ProjectStatus | null>(null);
  const [statusNotice, setStatusNotice] = useState('');

  const tfri = hasTfriRole(user);
  const canEdit = canEditProject(user?.id, project, tfri);
  const projectStatus: ProjectStatus = project?.status ?? 'Active';

  const collaboratorPickerUsers = useMemo(() => {
    const ownerId = project?.created_by_user_id;
    if (ownerId == null) return verifiedUsers;
    return verifiedUsers.filter((u) => u.id !== ownerId);
  }, [verifiedUsers, project?.created_by_user_id]);

  const loadProject = useCallback(async () => {
    if (!user?.id || !Number.isInteger(idNum) || idNum < 1) return;
    setLoading(true);
    setError('');
    setStatusNotice('');
    try {
      const p = await fetchProject(idNum, user.id);
      setProject(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load project.');
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, idNum]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedUsers()
      .then((list) => {
        if (!cancelled) setVerifiedUsers(list.map((u) => ({ id: u.id, username: u.username })));
      })
      .catch(() => {
        if (!cancelled) setVerifiedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openEdit = () => {
    if (!project) return;
    setEditName(project.name);
    setEditStart(toDateInputValue(project.start_date));
    setEditEnd(toDateInputValue(project.expected_end_date));
    setEditCollabIds(project.collaborators.map((c) => c.user_id));
    setEditProjectTypes([...project.project_types]);
    setEditEventLocation(project.event_location ?? '');
    setEditEventStart(project.event_start_time ?? '');
    setEditEventEnd(project.event_end_time ?? '');
    setEditError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditError('');
  };

  const submitEdit = async () => {
    if (!user?.id || !project) return;
    const name = editName.trim();
    if (!name) {
      setEditError('Project name is required.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const updated = await updateProject(project.id, {
        user_id: user.id,
        name,
        project_types: editProjectTypes,
        start_date: editStart.trim() || null,
        expected_end_date: editEnd.trim() || null,
        collaborator_user_ids: editCollabIds,
        event_location: editProjectTypes.includes('Event') ? editEventLocation.trim() || null : null,
        event_start_time: editProjectTypes.includes('Event') ? editEventStart.trim() || null : null,
        event_end_time: editProjectTypes.includes('Event') ? editEventEnd.trim() || null : null,
      });
      setProject(updated);
      closeEdit();
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save.');
    } finally {
      setEditSaving(false);
    }
  };

  const submitStatusChange = async (nextStatus: ProjectStatus) => {
    if (!user?.id || !project) return;
    const prevStatus = project.status;
    setStatusSaving(nextStatus);
    setEditError('');
    setStatusNotice('');
    try {
      const { tasks_updated: tasksUpdated = 0, ...updated } = await updateProject(project.id, {
        user_id: user.id,
        status: nextStatus,
      });
      setProject(updated);
      setEditOpen(false);
      setStatusNotice(statusChangeNotice(prevStatus, nextStatus, tasksUpdated));
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to change the project status.');
    } finally {
      setStatusSaving(null);
    }
  };

  const openAddCollab = () => {
    setAddCollabIds([]);
    setAddCollabError('');
    setAddCollabOpen(true);
  };

  const submitAddCollab = async () => {
    if (!user?.id || !project) return;
    const existing = project.collaborators.map((c) => c.user_id);
    const merged = Array.from(new Set([...existing, ...addCollabIds]));
    setAddCollabSaving(true);
    setAddCollabError('');
    try {
      const updated = await updateProject(project.id, {
        user_id: user.id,
        collaborator_user_ids: merged,
      });
      setProject(updated);
      setAddCollabOpen(false);
      setAddCollabIds([]);
    } catch (e) {
      setAddCollabError(e instanceof Error ? e.message : 'Failed to add collaborators.');
    } finally {
      setAddCollabSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <div className="project-detail-page-top">
            <button type="button" className="project-detail-back" onClick={() => navigate('/projects')}>
              ← Tasks Board
            </button>
          </div>
          <div className="page-content project-detail-content">
            {!user ? (
              <p className="page-subtitle">Log in to view this project.</p>
            ) : !Number.isInteger(idNum) || idNum < 1 ? (
              <p className="create-order-error" role="alert">
                Invalid project.
              </p>
            ) : loading ? (
              <p className="page-subtitle">Loading…</p>
            ) : error || !project ? (
              <p className="create-order-error" role="alert">
                {error || 'Project not found.'}
              </p>
            ) : (
              <>
                <div className="project-detail-title-row">
                  <h1 className="project-detail-title">{project.name}</h1>
                  {projectStatus !== 'Active' ? (
                    <span
                      className={`project-detail-status-pill project-detail-status-pill--${
                        projectStatus === 'On Hold' ? 'on-hold' : 'closed'
                      }`}
                    >
                      {projectStatus}
                    </span>
                  ) : null}
                </div>
                {statusNotice ? (
                  <div className="project-detail-status-note" role="status">
                    <span>{statusNotice}</span>
                    <button
                      type="button"
                      className="project-detail-status-note-dismiss"
                      aria-label="Dismiss"
                      onClick={() => setStatusNotice('')}
                    >
                      ×
                    </button>
                  </div>
                ) : projectStatus === 'On Hold' ? (
                  <div className="project-detail-status-note">
                    <span>
                      This project is on hold. Its open tasks were put on hold with it and go back to their earlier status
                      when the project is resumed.
                    </span>
                  </div>
                ) : null}

                <div className="project-detail-toolbar">
                  <div className="project-detail-tabs" role="tablist" aria-label="Project views">
                    {TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab}
                        className={`project-detail-tab${activeTab === tab ? ' project-detail-tab--active' : ''}`}
                        onClick={() => selectTab(tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  {canEdit ? (
                    <div className="project-detail-actions">
                      <button type="button" className="project-detail-secondary-btn" onClick={openAddCollab}>
                        Add collaborators
                      </button>
                      <button type="button" className="project-detail-primary-btn" onClick={openEdit}>
                        Edit project
                      </button>
                    </div>
                  ) : null}
                </div>

                <div
                  className="project-detail-panel"
                  role="tabpanel"
                  aria-label={activeTab}
                >
                  {activeTab === 'Overview' ? (
                    <div className="client-info-card">
                      <div className="client-info-header">
                        <h3 className="section-title">Project information</h3>
                      </div>
                      <div className="client-info-grid">
                        <div className="client-info-item client-info-item--full">
                          <label className="client-info-label">Project types</label>
                          <div className="client-info-value">
                            <ProjectTypePills types={project.project_types} />
                          </div>
                        </div>
                        <div className="client-info-item">
                          <label className="client-info-label">Start date</label>
                          <div className="client-info-value">{formatTaskDate(project.start_date)}</div>
                        </div>
                        <div className="client-info-item">
                          <label className="client-info-label">Expected end</label>
                          <div className="client-info-value">{formatTaskDate(project.expected_end_date)}</div>
                        </div>
                        {project.project_types.includes('Event') ? (
                          <>
                            <div className="client-info-item client-info-item--full">
                              <label className="client-info-label">Event location</label>
                              <div className="client-info-value">{project.event_location || '—'}</div>
                            </div>
                            <div className="client-info-item">
                              <label className="client-info-label">Event start</label>
                              <div className="client-info-value">{formatEventDateTime(project.event_start_time)}</div>
                            </div>
                            <div className="client-info-item">
                              <label className="client-info-label">Event end</label>
                              <div className="client-info-value">{formatEventDateTime(project.event_end_time)}</div>
                            </div>
                          </>
                        ) : null}
                        <div className="client-info-item client-info-item--full">
                          <label className="client-info-label">Owner</label>
                          <div className="client-info-value">{project.created_by_username ?? '—'}</div>
                        </div>
                        <div className="client-info-item client-info-item--full">
                          <label className="client-info-label">Collaborators</label>
                          <div className="client-info-value">
                            {project.collaborators.length === 0
                              ? 'None'
                              : project.collaborators.map((c) => c.username).join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : activeTab === 'Board' ? (
                    <ProjectBoardTab
                      key={projectStatus}
                      projectId={project.id}
                      viewerUserId={user?.id ?? 0}
                      canEditBoard={canEdit}
                    />
                  ) : activeTab === 'List' ? (
                    user?.id ? (
                      <ProjectListTab
                        key={projectStatus}
                        projectId={project.id}
                        viewerUserId={user.id}
                        canRemoveFromProject={canEdit}
                      />
                    ) : (
                      <p className="project-detail-panel-placeholder">Sign in to view project tasks.</p>
                    )
                  ) : activeTab === 'Calendar' ? (
                    user?.id ? (
                      <ProjectCalendarTab key={projectStatus} projectId={project.id} viewerUserId={user.id} />
                    ) : (
                      <p className="project-detail-panel-placeholder">Sign in to view the project calendar.</p>
                    )
                  ) : activeTab === 'Timeline' ? (
                    user?.id ? (
                      <ProjectTimelineTab key={projectStatus} projectId={project.id} viewerUserId={user.id} />
                    ) : (
                      <p className="project-detail-panel-placeholder">Sign in to view the timeline.</p>
                    )
                  ) : activeTab === 'Checklists' ? (
                    user?.id ? (
                      <ProjectChecklistsTab
                        projectId={project.id}
                        viewerUserId={user.id}
                        canEdit={canEdit}
                      />
                    ) : (
                      <p className="project-detail-panel-placeholder">Sign in to view checklists.</p>
                    )
                  ) : activeTab === 'Roles' ? (
                    user?.id ? (
                      <ProjectRolesTab
                        projectId={project.id}
                        viewerUserId={user.id}
                        canEdit={canEdit}
                      />
                    ) : (
                      <p className="project-detail-panel-placeholder">Sign in to view roles.</p>
                    )
                  ) : (
                    <p className="project-detail-panel-placeholder">Content for {activeTab} will go here.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <Modal isOpen={addCollabOpen} onClose={() => setAddCollabOpen(false)} title="Add collaborators" wide>
        <div className="modal-body">
          <p className="page-subtitle" style={{ marginTop: 0 }}>
            Search and add verified users. Existing collaborators stay on the project.
          </p>
          <TaskAssigneePicker
            users={collaboratorPickerUsers}
            value={addCollabIds}
            onChange={setAddCollabIds}
            addPlaceholder="Search user to add…"
          />
          {addCollabError ? (
            <p className="create-order-error" role="alert">
              {addCollabError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            disabled={addCollabSaving}
            onClick={() => setAddCollabOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="update-button"
            disabled={addCollabSaving}
            onClick={() => void submitAddCollab()}
          >
            {addCollabSaving ? 'Saving…' : 'Add to project'}
          </button>
        </div>
      </Modal>

      <Modal isOpen={editOpen} onClose={closeEdit} title="Edit project" wide>
        <div className="modal-body">
          <div className="form-group">
            <span className="form-label">Project types</span>
            <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Search and add categories; remove with × on each pill.
            </p>
            <ProjectTypePicker
              value={editProjectTypes}
              onChange={setEditProjectTypes}
              disabled={editSaving}
              addPlaceholder="Search type to add…"
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-edit-name" className="form-label">
              Project name
            </label>
            <input
              id="project-edit-name"
              type="text"
              className="form-input"
              value={editName}
              onChange={(e) => {
                setEditName(e.target.value);
                setEditError('');
              }}
              disabled={editSaving}
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-edit-start" className="form-label">
              Start date
            </label>
            <input
              id="project-edit-start"
              type="date"
              className="form-input"
              value={editStart}
              onChange={(e) => setEditStart(e.target.value)}
              disabled={editSaving}
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-edit-end" className="form-label">
              Expected end date
            </label>
            <input
              id="project-edit-end"
              type="date"
              className="form-input"
              value={editEnd}
              onChange={(e) => setEditEnd(e.target.value)}
              disabled={editSaving}
            />
          </div>
          {editProjectTypes.includes('Event') ? (
            <div className="projects-event-details">
              <h3 className="projects-event-details-title">Event details</h3>
              <div className="form-group">
                <label htmlFor="project-edit-event-location" className="form-label">
                  Location
                </label>
                <input
                  id="project-edit-event-location"
                  type="text"
                  className="form-input"
                  value={editEventLocation}
                  onChange={(e) => setEditEventLocation(e.target.value)}
                  placeholder="e.g. Austin Convention Center, Hall B"
                  disabled={editSaving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="project-edit-event-start" className="form-label">
                  Start time
                </label>
                <input
                  id="project-edit-event-start"
                  type="datetime-local"
                  className="form-input"
                  value={editEventStart}
                  onChange={(e) => setEditEventStart(e.target.value)}
                  disabled={editSaving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="project-edit-event-end" className="form-label">
                  End time
                </label>
                <input
                  id="project-edit-event-end"
                  type="datetime-local"
                  className="form-input"
                  value={editEventEnd}
                  onChange={(e) => setEditEventEnd(e.target.value)}
                  disabled={editSaving}
                />
              </div>
            </div>
          ) : null}
          <div className="form-group">
            <span className="form-label">Collaborators</span>
            <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Owner is not listed here. Add or remove collaborators below.
            </p>
            <TaskAssigneePicker
              users={collaboratorPickerUsers}
              value={editCollabIds}
              onChange={setEditCollabIds}
              addPlaceholder="Search user to add…"
            />
          </div>
          {canEdit ? (
            <div className="project-edit-status-toggle-row">
              <div className="project-edit-status-buttons">
                {projectStatus === 'Active' ? (
                  <button
                    type="button"
                    className="project-detail-status-toggle-btn project-detail-status-toggle-btn--hold"
                    disabled={editSaving || statusSaving != null}
                    onClick={() => void submitStatusChange('On Hold')}
                  >
                    {statusSaving === 'On Hold' ? 'Putting on hold…' : 'Put on hold'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="project-detail-status-toggle-btn project-detail-status-toggle-btn--open"
                    disabled={editSaving || statusSaving != null}
                    onClick={() => void submitStatusChange('Active')}
                  >
                    {statusSaving === 'Active'
                      ? projectStatus === 'On Hold'
                        ? 'Resuming…'
                        : 'Opening…'
                      : projectStatus === 'On Hold'
                        ? 'Resume project'
                        : 'Open project'}
                  </button>
                )}
                {projectStatus !== 'Closed' ? (
                  <button
                    type="button"
                    className="project-detail-status-toggle-btn project-detail-status-toggle-btn--close"
                    disabled={editSaving || statusSaving != null}
                    onClick={() => void submitStatusChange('Closed')}
                  >
                    {statusSaving === 'Closed' ? 'Closing…' : 'Close project'}
                  </button>
                ) : null}
              </div>
              {projectStatus === 'Active' ? (
                <p className="project-edit-status-hint">
                  Putting the project on hold also puts its Unassigned, To-Do and In Progress tasks on hold.
                </p>
              ) : projectStatus === 'On Hold' ? (
                <p className="project-edit-status-hint">
                  Resuming puts the tasks that were put on hold back to the status they had.
                </p>
              ) : null}
            </div>
          ) : null}
          {editError ? (
            <p className="create-order-error" role="alert">
              {editError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={editSaving || statusSaving != null} onClick={closeEdit}>
            Cancel
          </button>
          <button
            type="button"
            className="update-button"
            disabled={editSaving || statusSaving != null}
            onClick={() => void submitEdit()}
          >
            {editSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectDetail;
